import React, { useState, useEffect, useRef } from 'react';
import { ViewingEmail } from 'types';
import {
  Button,
  Link,
  SkeletonText,
  InlineNotification
} from 'carbon-components-react';
import { Undo16 } from '@carbon/icons-react';
import Util from 'utilities/helper.util';
import {
  PreviewContent,
  UploadAttachmentError,
  UploadingAttachment,
  DeleteAttachmentError
} from './localStore/types';
import EmailBody from './emailBody';

const EmailsViewer: React.FC<Props> = ({
  email,
  onClose,
  isAnonymousView,
  timezone,
  onPreviewOriginalEmail,
  onViewAttachment,
  onUploadAttachment,
  onClearDeleteAttachmentErrors,
  onClearUploadAttachmentErrors,
  uploadingAttachments,
  uploadAttachmentErrors,
  deletingAttachments,
  deleteAttachmentErrors,
  onClickExcludeFilesLink
}) => {
  const [isMultiple, setIsMultiple] = useState<boolean>(false);
  const phoneLinkRef = useRef<HTMLAnchorElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadLinkClick = () => {
    fileInputRef.current && fileInputRef.current.click();
  };

  const onUploadFileChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    const target = evt.target;
    if (target.files) {
      onUploadAttachment(target.files[0], id);
    }
  };

  useEffect(() => {
    setIsMultiple(email !== undefined && email.bodies.length > 1);
    email?.bodies.sort((itemA: any, itemB: any) =>
      itemB.created_at?.localeCompare(itemA.created_at)
    );
  }, [email]);

  email?.bodies.map((body: any) => {
    body.Attachment?.sort((itemA: any, itemB: any) =>
      itemA.presentation_name.localeCompare(itemB.presentation_name)
    );
  });

  return (
    <div className="emails-viewer-container">
      <div className="emails-viewer-buttons">
        <Button
          kind="tertiary"
          hasIconOnly
          renderIcon={Undo16}
          iconDescription="Back"
          tooltipPosition="top"
          onClick={() => onClose()}
        ></Button>
        {email?.assetOwnerContact && (
          <>
            <Button
              kind="tertiary"
              hasIconOnly
              iconDescription={email.assetOwnerContact}
              tooltipPosition="top"
              onClick={() =>
                phoneLinkRef.current && phoneLinkRef.current.click()
              }
            >
              Call asset owner
            </Button>
            <a
              ref={phoneLinkRef}
              href={`tel:${email.assetOwnerContact}`}
              style={{ display: 'none' }}
            >
              {email.assetOwnerContact}
            </a>
          </>
        )}
      </div>
      <div
        className={`emails-viewer-bodies${isMultiple ? ' is-multiple' : ''}`}
      >
        {email?.bodies.map((body: any, inx: number) => (
          <div key={`email-body-${inx}`} className="email-body">
            {inx === 0 && <div className="email-name">{email?.name}</div>}
            <div className="email-body-date">
              {Util.getNewDate(body?.created_at)
                .tz(timezone)
                .format('dddd DD/MM/YYYY h:mmA')}
            </div>
            {Array.isArray(body?.Attachment) && body.Attachment.length > 0 && (
              <div className="email-body-links">
                {body.Attachment.map((file: any) => (
                  <Link
                    key={`file-${file.id}`}
                    onClick={() => onViewAttachment(file.id, body.Attachment)}
                    className={`email-body-links-row ${
                      file.is_hidden ? 'email-body-links-excluded' : ''
                    }`}
                    title={
                      file.is_hidden
                        ? 'Attachment is excluded from combined downloads'
                        : ''
                    }
                  >
                    {file?.presentation_name}
                  </Link>
                ))}
              </div>
            )}
            {!isAnonymousView && body?.Attachment?.length > 0 && (
              <div className="file-excludes-link-section">
                <div className="divider"></div>
                <div className="file-excludes-link">
                  <Link
                    onClick={() =>
                      onClickExcludeFilesLink(
                        email?.name,
                        email?.enquiryId,
                        body.Attachment
                      )
                    }
                  >
                    Manage response attachments ...
                  </Link>
                </div>
              </div>
            )}
            <EmailBody
              body={body}
              isMultiple={isMultiple}
              onPreviewOriginalEmail={onPreviewOriginalEmail}
            />
          </div>
        ))}
        <div className="email-files-container">
          {((email &&
            Array.isArray(email.uploadedFiles) &&
            email.uploadedFiles.length > 0) ||
            uploadAttachmentErrors.length > 0 ||
            uploadingAttachments.length > 0) && (
            <div className="email-files-links">
              {email &&
                email.uploadedFiles.map((file: any) => {
                  const error = deleteAttachmentErrors.find(
                    (dae: DeleteAttachmentError) => dae.id === file.id
                  );
                  return (
                    <>
                      {deletingAttachments.includes(file.id) ? (
                        <SkeletonText
                          key={`uploaded-file-${file.id}`}
                          className="email-files-links-row"
                        />
                      ) : (
                        <>
                          {error ? (
                            <InlineNotification
                              kind="error"
                              title={error.error}
                              key={`e-${file.id}`}
                              lowContrast
                              className="email-files-links-row"
                              onCloseButtonClick={() =>
                                onClearDeleteAttachmentErrors(file.id)
                              }
                            />
                          ) : (
                            <Link
                              key={`uploaded-file-${file.id}`}
                              onClick={() => onViewAttachment(file.id)}
                              className="email-files-links-row"
                            >
                              {file?.presentation_name}
                            </Link>
                          )}
                        </>
                      )}
                    </>
                  );
                })}
              {uploadAttachmentErrors.map((uae: UploadAttachmentError) => {
                return uae.assetOwnerId !== undefined &&
                  uae.assetOwnerId === email?.id ? (
                  <InlineNotification
                    kind="error"
                    title={uae.error}
                    key={`e-${uae.uuid}`}
                    lowContrast
                    className="email-files-links-row"
                    onCloseButtonClick={() =>
                      onClearUploadAttachmentErrors(uae.uuid)
                    }
                  />
                ) : (
                  <></>
                );
              })}
              {uploadingAttachments.map((up: UploadingAttachment) => {
                return up.assetOwnerId !== undefined &&
                  up.assetOwnerId === email?.id ? (
                  <SkeletonText
                    key={up.uuid}
                    className="email-files-links-row"
                  />
                ) : (
                  <></>
                );
              })}
            </div>
          )}
          {!isAnonymousView && (
            <>
              <Link
                onClick={handleUploadLinkClick}
                className="email-files-link"
              >
                + Add a document
              </Link>
              <input
                type="file"
                className="bx--visually-hidden"
                ref={fileInputRef}
                onChange={(evt) =>
                  email?.id && onUploadFileChange(evt, email.id)
                }
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

type Props = {
  email?: ViewingEmail;
  timezone?: string;
  isAnonymousView: boolean;
  onClose: Function;
  onViewAttachment: (id: number, attachments?: Array<any>) => void;
  onPreviewOriginalEmail: (content: PreviewContent | undefined) => void;
  onUploadAttachment: (file: File, id?: number) => void;
  onClearDeleteAttachmentErrors: (id: number) => void;
  onClearUploadAttachmentErrors: (id: string) => void;
  uploadAttachmentErrors: UploadAttachmentError[];
  uploadingAttachments: UploadingAttachment[];
  deletingAttachments: number[];
  deleteAttachmentErrors: DeleteAttachmentError[];
  onClickExcludeFilesLink: Function;
};

export default React.memo(EmailsViewer);
